import { useToast } from '@/components/ui/hooks/use-toast.js';
import {
  createCoachPool,
  deleteCoachPool,
  getCoachPools,
  setCoachPoolActive,
  updateCoachPool,
} from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import type { CoachPoolFilters, CoachPoolWithMatchCount, CreateCoachPool } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { SetCoachPoolActiveDialog } from './coach-pool-dialogs.js';
import { RemoveCoachPoolDialog } from './remove-coach-pool-dialog.js';

interface SaveCoachPoolInput {
  mode: 'create' | 'edit';
  id?: string;
  title: string;
  description: string;
  filters: CoachPoolFilters;
}

type CoachPoolAction =
  | { type: 'save'; input: SaveCoachPoolInput }
  | { type: 'remove'; id: string }
  | { type: 'setActive'; id: string; isActive: boolean };

const coachPoolSaveInput = ({
  title,
  description,
  filters,
}: Pick<SaveCoachPoolInput, 'title' | 'description' | 'filters'>): CreateCoachPool => {
  const trimmedDescription = description.trim();
  const query = filters.query?.trim();
  const timeRanges = filters.timeRanges?.length ? filters.timeRanges : undefined;
  const backgroundCheckStatuses = filters.backgroundCheckStatuses ?? [];

  return {
    title: title.trim(),
    ...(trimmedDescription ? { description: trimmedDescription } : {}),
    ...(query ? { query } : {}),
    statuses: filters.statuses ?? [],
    subStatuses: filters.subStatuses ?? [],
    overallGrades: (filters.overallGrades ?? []).filter((grade) => grade != null),
    classEligibilities: filters.classEligibilities ?? [],
    backgroundCheckStatuses,
    ...(backgroundCheckStatuses.length
      ? { backgroundCheckStatusesLogicalOperator: filters.backgroundCheckStatusesLogicalOperator ?? 'and' }
      : {}),
    ...(typeof filters.googleMeetTechCheck === 'boolean' ? { googleMeetTechCheck: filters.googleMeetTechCheck } : {}),
    ...(typeof filters.hasAvailability === 'boolean' ? { hasAvailability: filters.hasAvailability } : {}),
    ...(timeRanges
      ? {
          timeRanges,
          untakenTeachersOnly: filters.untakenTeachersOnly ?? true,
          coachAvailabilityType: filters.coachAvailabilityType ?? 'regular',
        }
      : {}),
    states: filters.states ?? [],
  };
};

async function runCoachPoolAction(action: CoachPoolAction): Promise<void> {
  switch (action.type) {
    case 'save': {
      const data = coachPoolSaveInput(action.input);
      if (action.input.mode === 'edit') {
        if (!action.input.id) {
          throw new Error('Missing pool id');
        }
        await updateCoachPool({ id: action.input.id, data });
        return;
      }
      await createCoachPool(data);
      return;
    }
    case 'remove':
      await deleteCoachPool(action.id);
      return;
    case 'setActive':
      await setCoachPoolActive({ id: action.id, isActive: action.isActive });
  }
}

function successTitle(action: CoachPoolAction): string {
  switch (action.type) {
    case 'save':
      return action.input.mode === 'edit' ? 'Teacher pool updated' : 'Teacher pool created';
    case 'remove':
      return 'Teacher pool removed';
    case 'setActive':
      return action.isActive ? 'Teacher pool activated' : 'Teacher pool deactivated';
  }
}

function errorTitle(action: CoachPoolAction): string {
  switch (action.type) {
    case 'save':
      return 'Could not save the teacher pool';
    case 'remove':
      return 'Could not remove the teacher pool';
    case 'setActive':
      return 'Could not update the teacher pool';
  }
}

// One mutation for creating, updating, removing, and activating a coach pool.
export const useCoachPoolMutation = ({ onRemoved }: { onRemoved?: () => void } = {}) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onRemovedRef = useRef(onRemoved);
  onRemovedRef.current = onRemoved;
  const [removePool, setRemovePool] = useState<CoachPoolWithMatchCount | null>(null);
  const [togglePool, setTogglePool] = useState<CoachPoolWithMatchCount | null>(null);
  const { mutate, isPending, variables } = useMutation({
    mutationFn: runCoachPoolAction,
    onSuccess: async (_result, action) => {
      if (action.type === 'remove') {
        setRemovePool(null);
        onRemovedRef.current?.();
      } else if (action.type === 'setActive') {
        setTogglePool(null);
      }
      toast({ title: successTitle(action) });
      await queryClient.invalidateQueries(getCoachPools());
    },
    onError: (error, action) => {
      toast({ title: airServerErrorFrom(error)?.userFacingMessage ?? errorTitle(action) });
    },
  });

  const savePool = useCallback(
    (input: SaveCoachPoolInput, options?: { onSuccess?: () => void }) => {
      mutate({ type: 'save', input }, options?.onSuccess ? { onSuccess: options.onSuccess } : undefined);
    },
    [mutate],
  );

  const closeRemove = () => {
    if (!isPending) {
      setRemovePool(null);
    }
  };
  const closeToggle = () => {
    if (!isPending) {
      setTogglePool(null);
    }
  };

  const removingId = isPending && variables?.type === 'remove' ? variables.id : undefined;
  const updatingId = isPending && variables?.type === 'setActive' ? variables.id : undefined;

  return {
    savePool,
    isSavingPool: isPending && variables?.type === 'save',
    requestRemove: setRemovePool,
    isRemoving: (poolId: string) => removingId === poolId,
    requestToggle: setTogglePool,
    isUpdating: (poolId: string) => updatingId === poolId,
    dialogs:
      removePool || togglePool ? (
        <>
          {removePool ? (
            <RemoveCoachPoolDialog
              pool={removePool}
              isPending={removingId === removePool.id}
              onOpenChange={(open) => {
                if (!open) {
                  closeRemove();
                }
              }}
              onConfirm={() => mutate({ type: 'remove', id: removePool.id })}
            />
          ) : null}
          {togglePool ? (
            <SetCoachPoolActiveDialog
              pool={togglePool}
              isPending={updatingId === togglePool.id}
              onOpenChange={(open) => {
                if (!open) {
                  closeToggle();
                }
              }}
              onConfirm={() => mutate({ type: 'setActive', id: togglePool.id, isActive: !togglePool.isActive })}
            />
          ) : null}
        </>
      ) : null,
  };
};
